import api from "./index";

// ---------------- Records (grid) ----------------
// params: view, status, list, temp, suburb, beds, type, callbacks_due, q,
// and `agent` — the id of a colleague whose Database has been shared with the
// caller, which swaps the grid over to that agent's records.
export const getProspects = (params = {}) =>
  api.get("api/prospecting/records/", { params });

export const getProspect = (id) =>
  api.get(`api/prospecting/records/${id}/`);

// `agent` files the new record under the owner of the shared Database the
// agent is currently viewing (needs edit rights on that share).
export const createProspect = (data, agent) =>
  api.post("api/prospecting/records/", agent ? { ...data, agent } : data);

export const updateProspect = (id, data) =>
  api.patch(`api/prospecting/records/${id}/`, data);

export const deleteProspect = (id) =>
  api.delete(`api/prospecting/records/${id}/`);

export const addProspectNote = (id, body) =>
  api.post(`api/prospecting/records/${id}/notes/`, { body });

// preset: '1d'|'1w'|'1m'|'3m'|'6m'|'12m'  OR  date: 'YYYY-MM-DD'
// plus optional time: 'HH:MM' and reason: contact|follow_up|appointment|action_required|review
export const setProspectNextCall = (id, payload) =>
  api.post(`api/prospecting/records/${id}/next-call/`, payload);

export const setProspectReminder = (id, payload) =>
  api.post(`api/prospecting/records/${id}/reminder/`, payload);

export const getProspectMatches = (id) =>
  api.get(`api/prospecting/records/${id}/matches/`);

// The CRM contact mirrored from a Database row, created on the spot if the link is
// missing. person: 'primary' | 'secondary' (the 2nd owner/buyer on the row).
// Returns { contact_id, contact_name } — what the Buying Preferences editor needs.
export const getProspectContact = (id, person = "primary") =>
  api.post(`api/prospecting/records/${id}/contact/`, { person });

// Mark one person on a row Do not contact, or lift the flag again. The flag is
// stored on the mirrored CRM contact, so it also excludes them from every
// campaign audience and blacklists their email/mobile against a re-import
// (Paul Jimoulis, Monday 12931774512). Returns the refreshed record.
export const setProspectUnsubscribed = (id, person = "primary", unsubscribed = true) =>
  api.post(`api/prospecting/records/${id}/unsubscribe/`, { person, unsubscribed });

// The owner/property details to open the Add Appraisal page with.
export const getProspectAppraisalPrefill = (id) =>
  api.get(`api/prospecting/records/${id}/convert/`);

// Retire the record from the grid. Pass { property_id } to link the appraisal the
// agent just saved on the Add Appraisal page; with no payload the server creates
// the appraisal itself (grid bulk convert).
export const convertProspect = (id, payload = {}) =>
  api.post(`api/prospecting/records/${id}/convert/`, payload);

export const restoreProspect = (id) =>
  api.post(`api/prospecting/records/${id}/restore/`);

// The other direction: an appraisal in the pipeline back onto a Database tab
// (Paul Jimoulis, CRM-2026-00070). payload: { property_id, view, list }.
// An appraisal that came FROM the Database reuses its original row rather than
// creating a second copy of the same owner, so the round trip is a round trip.
export const addAppraisalToDatabase = (payload) =>
  api.post("api/prospecting/records/from-appraisal/", payload);

// action: 'convert' | 'delete' | 'add_to_list' | 'colour' | 'preferences'
//       | 'reminder' | 'unsubscribe' | 'email' | 'sms'
export const bulkProspectAction = (payload) =>
  api.post("api/prospecting/records/bulk/", payload);

// ---------------- Lists ----------------
export const getProspectLists = (view, agent) =>
  api.get("api/prospecting/lists/", { params: { view, agent } });

export const createProspectList = (data) =>
  api.post("api/prospecting/lists/", data);

export const updateProspectList = (id, data) =>
  api.patch(`api/prospecting/lists/${id}/`, data);

export const deleteProspectList = (id) =>
  api.delete(`api/prospecting/lists/${id}/`);

// Combine two tabs: every row of list `id` moves into list `into`, then `id`
// is removed.
export const mergeProspectList = (id, into) =>
  api.post(`api/prospecting/lists/${id}/merge/`, { into });

// Retitle a whole suburb heading in one call: { view, group_name, new_name }.
// One request rather than a PATCH per tab, because the heading is what a group
// share is keyed on — renaming tab by tab can lose the share in between.
export const renameProspectGroup = (payload) =>
  api.patch("api/prospecting/lists/group/", payload);

// ---------------- Columns + saved layout ----------------
export const getCustomColumns = (view, agent) =>
  api.get("api/prospecting/columns/", { params: { view, agent } });

export const createCustomColumn = (data) =>
  api.post("api/prospecting/columns/", data);

export const updateCustomColumn = (id, data) =>
  api.patch(`api/prospecting/columns/${id}/`, data);

export const deleteCustomColumn = (id) =>
  api.delete(`api/prospecting/columns/${id}/`);

export const getViewConfig = (view, agent) =>
  api.get("api/prospecting/view-config/", { params: { view, agent } });

export const saveViewConfig = (data) =>
  api.put("api/prospecting/view-config/", data);

// ---------------- Import ----------------
export const getImportFields = (view) =>
  api.get("api/prospecting/import/fields/", { params: { view } });

export const previewImport = (formData) =>
  api.post("api/prospecting/import/preview/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 30000,
  });

export const commitImport = (payload) =>
  api.post("api/prospecting/import/commit/", payload, { timeout: 60000 });

// Re-run the data check for a changed column mapping. Writes nothing.
export const checkImport = (payload) =>
  api.post("api/prospecting/import/check/", payload, { timeout: 30000 });

// The spreadsheet as uploaded for an import job (a blob download).
export const downloadImportFile = (jobId) =>
  api.get(`api/prospecting/import/jobs/${jobId}/file/`, { responseType: "blob" });

// Whole-workbook auto-clean import (messy multi-tab xlsx)
export const previewWorkbook = (formData) =>
  api.post("api/prospecting/import/workbook-preview/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });

export const commitWorkbook = (formData) =>
  api.post("api/prospecting/import/workbook-commit/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000, // large messy workbooks can take a while
  });

// Info about the most recent upload for a view (+ total records).
export const getLastImport = (view) =>
  api.get("api/prospecting/import/last/", { params: { view } });

// mode: 'last' (undo last upload) | 'all' (clear every record in the view)
export const deleteImport = (view, mode = "last") =>
  api.delete("api/prospecting/import/last/", { params: { view, mode }, timeout: 120000 });

// ---------------- Map ----------------
export const getProspectMap = (params = {}) =>
  api.get("api/prospecting/map/", { params });

// ---------------- Sharing ----------------
// One payload drives both the Share modal and the database switcher:
//   shared_by_me   — colleagues who can see MY WHOLE Database (revocable)
//   scoped_shares  — the per-group / per-tab grants, for the badges on the tabs
//   shared_with_me — databases I can open, with their record counts
//   agents         — colleagues I could invite, each flagged `shared`
export const getDatabaseShares = () => api.get("api/prospecting/shares/");

// Who can see ONE group (suburb heading) or ONE list, rather than the whole
// Database: { scope: 'group', view, group_name } or { scope: 'list', list }.
export const getShareTarget = (target) =>
  api.get("api/prospecting/shares/", { params: target });

// { agent_ids: [1,2], can_edit: bool, both_ways: bool }
// Add scope/view/group_name/list to share just that group or tab, or
// { everyone: true } to open it to the whole agency.
export const shareDatabase = (payload) =>
  api.post("api/prospecting/shares/", payload);

export const updateDatabaseShare = (id, payload) =>
  api.patch(`api/prospecting/shares/${id}/`, payload);

export const revokeDatabaseShare = (id) =>
  api.delete(`api/prospecting/shares/${id}/`);

// Stamps last_opened_at so the owner can see a share is actually being used.
export const markSharedDatabaseOpened = (agentId) =>
  api.post("api/prospecting/shares/opened/", { agent_id: agentId });

// Super admin hands a departing agent's whole Database to another agent
// (Christian Gravias, Monday 13202305277).
export const getDatabaseTransferPreview = (fromAgentId) =>
  api.get("api/prospecting/transfer/", { params: { from: fromAgentId } });

export const transferDatabase = (fromAgentId, toAgentId) =>
  api.post("api/prospecting/transfer/", { from_agent: fromAgentId, to_agent: toAgentId });

// ---------------- Public Properties ----------------
// The Database's own property layer: every owner row with an address, as a
// property profile in the CRM. Deliberately NOT a property_management Property
// — see prospecting/services/public_properties.py for why. Kept in this module
// because the row is the source of truth, so the endpoints are Database ones.
//
// Not to be confused with api/publicApi.js `getPublicProperties`, which is the
// external Public Property Search (comparable sales) feed.
//
// params: search, suburb, type, agent, limit, offset
export const getPublicPropertyProfiles = (params = {}) =>
  api.get("api/prospecting/public-properties/", { params });

export const getPublicPropertyProfile = (recordId) =>
  api.get(`api/prospecting/public-properties/${recordId}/`);
